// core
import { ReactNode } from 'react'
// libraries
import styled from '@emotion/styled'
import { ThemeProvider } from '@emotion/react'
import Geocode from 'react-geocode'
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'
import { createUploadLink } from 'apollo-upload-client'
import { onError } from '@apollo/client/link/error'
import { toast } from 'react-toastify'
import {
  Provider as RollbarProvider,
  ErrorBoundary as RollbarErrorBoundary,
} from '@rollbar/react'
import { setContext } from '@apollo/client/link/context'
import {
  ApolloProvider,
  ApolloClient,
  InMemoryCache,
  ApolloLink,
} from '@apollo/client'
import Rollbar from 'rollbar'
import { v4 as uuidv4 } from 'uuid'
// partials
import { FormPage } from './FormPage/FormPage'
import { ErrorFallback } from 'ErrorPage/ErrorFallback'
// components
import { Text } from 'components/atoms/Typography'
import { ToastContainer } from 'components'
// utils
import { theme, GlobalStyles } from 'utils'
// styles
import './App.css'

Geocode.setApiKey(process.env.REACT_APP_GEOCODING_GOOGLE_KEY || '')

const rollbarConfig = {
  accessToken: process.env.REACT_APP_ROLLBAR_API_KEY,
  environment: process.env.NODE_ENV,
}

const rollbar = new Rollbar()

const headersLink = setContext((_, { headers }) => {
  return {
    headers: {
      ...headers,
      'X-request-id': uuidv4(),
      'X-app-with': 'list-venue',
    },
  }
})

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (networkError) {
    toast.error('Error ocurred')
  }

  if (graphQLErrors) {
    graphQLErrors.forEach((error) => {
      rollbar.configure({
        ...rollbarConfig,
        captureUncaught: true,
        captureUnhandledRejections: true,
        payload: {
          requestId: operation.getContext().headers['X-request-id'],
        },
      })

      const response = rollbar.error(error.message, error)

      return toast.error(
        <>
          <Text as="p">{error.message}</Text>
          <Text fontSize="s" as="p">
            {response.uuid}
          </Text>
        </>
      )
    })
  }
})

const client = new ApolloClient({
  cache: new InMemoryCache(),
  link: ApolloLink.from([
    errorLink,
    headersLink,
    //@ts-ignore
    createUploadLink({
      uri: process.env.REACT_APP_API_URL,
    }),
  ]),
})

const SelectPortal = styled('div')`
  z-index: 50;
  text-align: left;
`

function App() {
  const RollbarConditionalProvider = ({ children }: { children: ReactNode }) =>
    process.env.NODE_ENV === 'production' ? (
      <RollbarProvider config={rollbarConfig}>{children}</RollbarProvider>
    ) : (
      <>{children}</>
    )

  return (
    <div className="App">
      <RollbarConditionalProvider>
        <RollbarErrorBoundary fallbackUI={ErrorFallback}>
          <ApolloProvider client={client}>
            <ThemeProvider theme={theme}>
              <GlobalStyles />
              <ToastContainer />

              <GoogleReCaptchaProvider
                reCaptchaKey={process.env.REACT_APP_CAPTCHA_SITE_KEY || ''}
              >
                <FormPage />
              </GoogleReCaptchaProvider>

              {/* NOTE: this is used for select menu to apply query selector for createPortal  */}
              <SelectPortal id="select-menu" />
            </ThemeProvider>
          </ApolloProvider>
        </RollbarErrorBoundary>
      </RollbarConditionalProvider>
    </div>
  )
}

export default App
